import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/Auth/AppModule';
import { AuthModule } from '@/store/Auth/AuthModule';
import ProductIssue from '@/models/Products/ProductIssues/ProductIssue';
import CustomerAccountSummary from '@/models/Accounts/DepositAccounts/DepositAccountSummary';
import ProductFeatureVisiblity from '@/models/Products/ProductFeatureVisiblity';
import ListUtils from '@/lib/listUtils';
import HistoryUtils from '@/lib/HistoryUtils';
import PermissionUtils from '@/lib/permissionUtils';
import TransactionalFee from '@/models/Products/ProductFees/TransactionalFee';
import ScheduledFee from '@/models/Products/ProductFees/ScheduledFee';
import ManualFee from '@/models/Products/ProductFees/ManualFee';
import HistoryEntry from '@/models/Shared/HistoryEntry';
import GetHistoryResponse from '@/models/Shared/GetHistoryResponse';
import UpdateProductIssueRequest from '@/models/Products/ProductIssues/UpdateProductIssueRequest';

export interface IProductIssueDetailsState {
    loading: boolean;
    loadingHistory: boolean;
    productIssue: ProductIssue;
    productIssueAccounts: CustomerAccountSummary[];
    totalProductIssueAccountsPages: number;
    productFeatureVisibility: ProductFeatureVisiblity;
    generalFeeSettingsAvailable: boolean;
    currentTransactionalFee: TransactionalFee;
    currentScheduledFee: ScheduledFee;
    currentManualFee: ManualFee;
    totalTransactionalFeesPages: number;
    totalScheduledFeesPages: number;
    totalManualFeesPages: number;
    history: HistoryEntry[];
    pageSize: number;
}

@Module({ dynamic: true, store, name: 'ProductIssueDetailsModule', namespaced: true })
class ProductIssueDetailsStore extends VuexModule implements IProductIssueDetailsState {

    public loading: boolean = true;
    public loadingHistory: boolean = false;
    public productIssue: ProductIssue = new ProductIssue();
    public productIssueAccounts: CustomerAccountSummary[] = [];
    public totalProductIssueAccountsPages: number = 1;
    public productFeatureVisibility = new ProductFeatureVisiblity();
    public generalFeeSettingsAvailable: boolean = true;
    public currentTransactionalFee: TransactionalFee = new TransactionalFee();
    public currentScheduledFee: ScheduledFee = new ScheduledFee();
    public currentManualFee: ManualFee = new ManualFee();
    public totalTransactionalFeesPages: number = 1;
    public totalScheduledFeesPages: number = 1;
    public totalManualFeesPages: number = 1;
    public history: HistoryEntry[] = [];
    public pageSize: number = PermissionUtils.getPageSize();

    @Action
    async getProductIssue(id: string) {

        this.setLoading(true);

        await ajax.get(`/portal-backend/fi-api/product-issues/${id}`)
        .then(response => {
            this.context.commit("setProductIssue", response.data);

            // If any of the fee types are present in the product then preselect
            // them as the current fee, otherwise reset the fee back to empty
            if (response.data.generalFeeSettings) {
                const { transactionalFees, scheduledFees, manualFees } = response.data.generalFeeSettings;
                if (transactionalFees?.length > 0) {
                    this.context.commit("setCurrentTransactionalFee", transactionalFees[0]);
                    this.context.commit("setTransactionalFeesTotalPages", transactionalFees.length);
                }
                else {
                    this.context.commit("setCurrentTransactionalFee", new TransactionalFee);
                    this.context.commit("setTransactionalFeesTotalPages", 0);
                }
                if (scheduledFees?.length > 0) {
                    this.context.commit("setCurrentScheduledFee", scheduledFees[0]);
                    this.context.commit("setScheduledFeesTotalPages", scheduledFees.length);
                }
                else {
                    this.context.commit("setCurrentScheduledFee", new ScheduledFee);
                    this.context.commit("setScheduledFeesTotalPages", 0);
                }
                if (manualFees?.length > 0) {
                    this.context.commit("setCurrentManualFee", manualFees[0]);
                    this.context.commit("setManualFeesTotalPages", manualFees.length);
                }
                else {
                    this.context.commit("setCurrentManualFee", new ManualFee);
                    this.context.commit("setManualFeesTotalPages", 0);
                }
            }
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async getHistory() {

        this.setLoadingHistory(true);

        await ajax.get(`/portal-backend/fi-api/internal/product-issues/${this.productIssue.id}/history`)
        .then(response => {
            this.context.commit("setHistory", response.data);
            this.setLoadingHistory(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingHistory(false);
        });
    }

    @Action
    async editProductIssue(request: UpdateProductIssueRequest) {

        this.setLoadingHistory(true);

        await ajax.patch(`/portal-backend/fi-api/internal/product-issues/${this.productIssue.id}`, request)
        .then(response => {
            this.context.commit("setProductIssue", response.data);
            this.setLoadingHistory(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingHistory(false);
        });
    }

    @Action
    async getProductIssueAccounts(pageNumber: number) {

        //this.setLoading(true);

        // If UX API Access is allowed, use UX API
        if (AuthModule.me.institution.config.uxApiAccess && PermissionUtils.allowUxApiAccess()) {
            await ajax.get(`/portal-backend/ux-api/accounts?pageSize=${this.pageSize}&pageNumber=${pageNumber}&productId=${this.productIssue.productId}&productIssueId=${this.productIssue.id}`)
            .then(response => {
                this.context.commit("setProductIssueAccounts", response.data.accounts);
                this.context.commit("setProductIssueAccountsTotalPages", response.data.records.totalRecords);
                //this.setLoading(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                //this.setLoading(false);
            });
        }
        // Else get Customer Accounts from FI instead
        else {
            await ajax.get(`/portal-backend/fi-api/accounts?pageSize=${this.pageSize}&pageNumber=${pageNumber}&productId=${this.productIssue.productId}&productIssueId=${this.productIssue.id}`)
            .then(response => {
                this.context.commit("setProductIssueAccounts", response.data.results);
                this.context.commit("setProductIssueAccountsTotalPages", response.data.records.totalRecords);
                //this.setLoading(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                //this.setLoading(false);
            });
        }
    }

    @Action
    cleanState() {
        this.context.commit("resetState");
    }

    @Mutation
    resetState() {
        this.loading = true;
        this.loadingHistory = true;
        this.loading = true;
        this.productIssue = new ProductIssue();
        this.productIssueAccounts = [];
        this.totalProductIssueAccountsPages = 1;
        this.productFeatureVisibility = new ProductFeatureVisiblity();
        this.currentTransactionalFee = new TransactionalFee();
        this.currentScheduledFee = new ScheduledFee();
        this.currentManualFee = new ManualFee();
        this.totalTransactionalFeesPages = 1;
        this.totalScheduledFeesPages = 1;
        this.totalManualFeesPages = 1;
        this.history = [];
    }

    @Mutation
    private async setLoading(loading: boolean) {
        this.loading = loading;
    }

    @Mutation
    private async setLoadingHistory(loading: boolean) {
        this.loadingHistory = loading;
    }

    @Mutation
    private async setProductIssue(productIssue: ProductIssue) {
        if (productIssue.generalFeeSettings) {
            this.generalFeeSettingsAvailable = true;
        }
        this.productIssue = productIssue;
        this.productFeatureVisibility = ListUtils.productFeatureVisibility(productIssue.generalSettings.subType);
    }

    @Mutation
    private async setCurrentTransactionalFee(transactionalFee: TransactionalFee) {
        this.currentTransactionalFee = transactionalFee;
    }

    @Mutation
    private async setCurrentScheduledFee(scheduledFee: ScheduledFee) {
        this.currentScheduledFee = scheduledFee;
    }

    @Mutation
    private async setCurrentManualFee(manualFee: ManualFee) {
        this.currentManualFee = manualFee;
    }

    @Mutation
    private async setTransactionalFeesTotalPages(total: number) {
        this.totalTransactionalFeesPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setScheduledFeesTotalPages(total: number) {
        this.totalScheduledFeesPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setManualFeesTotalPages(total: number) {
        this.totalManualFeesPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setHistory(response: GetHistoryResponse) {
        const history = HistoryUtils.cleanHistory(response);
        this.history = history;
    }

    @Mutation
    private async setProductIssueAccounts(response: CustomerAccountSummary[]) {
        if (response) {
            this.productIssueAccounts = response;
        }
    }

    @Mutation
    private async setProductIssueAccountsTotalPages(total: number) {
        this.totalProductIssueAccountsPages = Math.ceil(total/this.pageSize);
    }

}

export const ProductIssueDetailsModule = getModule(ProductIssueDetailsStore);